/**
 * Mob Visual Archetype System — a data-driven creature-rendering framework.
 *
 * An **archetype** (idle motion + hit-reaction + death + telegraph hooks) carries
 * family-typical defaults so a mob config is ~5 lines. The renderer supplies only
 * the look (SVG + tint + scale); the archetype provides the behavior.
 *
 * Mirrors the atmosphere biome-archetype idea: family defaults + per-instance
 * distinguishing values. See `docs/design/systems/gameplay/mob-visual-archetypes.md`.
 *
 * Lane E S3 — the Hollow Coil mob vibe layer. The Coil roster is the first
 * consumer: cozy fauna (pig/snail/slime) → spooky undead-lite (shadow_bat/
 * evil_eye/dark_stump) → boss (hollow_keeper).
 */

// ── Archetypes ────────────────────────────────────────────────────────

import { BRANCH_MOB_VISUALS } from './branchMobVisuals';

export type MobArchetype = 'grounder' | 'floater' | 'skitterer' | 'lumberer' | 'phantom';

/** Pre-attack wind-up self-tell (ArchetypeBehavior.computeTelegraphPose). */
export type TelegraphCue = 'flash' | 'grow' | 'crouch' | 'flicker' | 'slam';

// ── Visual Config ─────────────────────────────────────────────────────

export interface MobVisualConfig {
  /** Drives idle + reaction behavior defaults. */
  archetype: MobArchetype;
  /** The core look — which SVG generator key + tint/scale overrides. */
  art: { svgKey: string; tint?: number; scale?: number; anchorY?: number };
  /** Idle motion — archetype-defaulted; override per mob if needed.
   *  `sway` is the horizontal sway/drift amplitude in px. */
  idle?: { bobAmpPx?: number; bobPeriodMs?: number; sway?: number };
  /** Hit-reaction — squash-stretch + flash + recoil on combat:hit.
   *  mode 'phase' = spectral alpha-dip instead of flash (phantoms; also
   *  shadow_bat, a floater that phases through hits). */
  hitReact?: { squash?: number; flashColor?: number; recoilPx?: number; mode?: 'flash' | 'phase' };
  /** Death animation before despawn. */
  death?: { mode: 'dissolve' | 'shatter' | 'sink' | 'poof'; durMs?: number };
  /** Pre-attack wind-up self-tell. */
  telegraph?: { tellMs?: number; cue?: TelegraphCue };
  /** Ambient signature — a faint aura / particles so the creature *feels* wrong
   *  (or cozy). The per-creature mood layer. */
  vibe?: { auraColor?: number; auraAlpha?: number; particleKind?: string };
}

// ── Archetype Defaults ────────────────────────────────────────────────

const ARCHETYPE_DEFAULTS: Record<MobArchetype, Partial<MobVisualConfig>> = {
  /** Plants + breathes — grounded critters (pig, snail, slime, boar, mushroom). */
  grounder: {
    idle: { bobAmpPx: 2, bobPeriodMs: 2000, sway: 1 },
    hitReact: { squash: 0.2, flashColor: 0xff5555, recoilPx: 8, mode: 'flash' },
    death: { mode: 'sink', durMs: 600 },
    telegraph: { tellMs: 500, cue: 'crouch' },
    vibe: { particleKind: 'dust' },
  },
  /** Bobs + drifts + tilts — flying/hovering creatures (jellyfish, bee, bat). */
  floater: {
    idle: { bobAmpPx: 4, bobPeriodMs: 1500, sway: 3 },
    hitReact: { squash: 0.2, flashColor: 0xff5555, recoilPx: 6, mode: 'flash' },
    death: { mode: 'dissolve', durMs: 500 },
    telegraph: { tellMs: 400, cue: 'grow' },
    vibe: { particleKind: 'sparkle' },
  },
  /** Fast skitter — small fast movers (future content; none in the Coil). */
  skitterer: {
    idle: { bobAmpPx: 3, bobPeriodMs: 400, sway: 1 },
    hitReact: { squash: 0.25, flashColor: 0xff5555, recoilPx: 8, mode: 'flash' },
    death: { mode: 'poof', durMs: 400 },
    telegraph: { tellMs: 200, cue: 'flash' },
  },
  /** Slow heavy sway, no bounce — massive rigid bodies (dark_stump, keeper). */
  lumberer: {
    idle: { bobAmpPx: 0, bobPeriodMs: 4000, sway: 2 },
    hitReact: { squash: 0.3, flashColor: 0xff5555, recoilPx: 4, mode: 'flash' },
    death: { mode: 'shatter', durMs: 1000 },
    telegraph: { tellMs: 1500, cue: 'crouch' },
    vibe: { particleKind: 'heavy_dust' },
  },
  /** Fades + pulses — eldritch/undead (evil_eye). No flash — they phase. */
  phantom: {
    idle: { bobAmpPx: 0, bobPeriodMs: 800, sway: 0 },
    hitReact: { squash: 0, flashColor: 0xaa00ff, recoilPx: 3, mode: 'phase' },
    death: { mode: 'dissolve', durMs: 600 },
    telegraph: { tellMs: 300, cue: 'flicker' },
    vibe: { auraAlpha: 0.15 },
  },
};

// ── Config Resolution ─────────────────────────────────────────────────

/** The fully-resolved config — every field filled with archetype defaults. */
export interface ResolvedMobVisualConfig {
  archetype: MobArchetype;
  art: { svgKey: string; tint: number; scale: number; anchorY: number };
  idle: { bobAmpPx: number; bobPeriodMs: number; sway: number };
  hitReact: { squash: number; flashColor: number; recoilPx: number; mode: 'flash' | 'phase' };
  death: { mode: 'dissolve' | 'shatter' | 'sink' | 'poof'; durMs: number };
  telegraph: { tellMs: number; cue: TelegraphCue };
  vibe: { auraColor?: number; auraAlpha: number; particleKind?: string };
}

/** Resolve a MobVisualConfig by merging archetype defaults with per-mob overrides. */
export function resolveMobVisualConfig(cfg: MobVisualConfig): ResolvedMobVisualConfig {
  const defaults = ARCHETYPE_DEFAULTS[cfg.archetype] ?? ARCHETYPE_DEFAULTS.grounder;
  return {
    archetype: cfg.archetype,
    art: {
      svgKey: cfg.art.svgKey,
      tint: cfg.art.tint ?? 0xffffff,
      scale: cfg.art.scale ?? 1,
      anchorY: cfg.art.anchorY ?? 0.5,
    },
    idle: {
      bobAmpPx: cfg.idle?.bobAmpPx ?? defaults.idle?.bobAmpPx ?? 2,
      bobPeriodMs: cfg.idle?.bobPeriodMs ?? defaults.idle?.bobPeriodMs ?? 1000,
      sway: cfg.idle?.sway ?? defaults.idle?.sway ?? 0,
    },
    hitReact: {
      squash: cfg.hitReact?.squash ?? defaults.hitReact?.squash ?? 0.15,
      flashColor: cfg.hitReact?.flashColor ?? defaults.hitReact?.flashColor ?? 0xff5555,
      recoilPx: cfg.hitReact?.recoilPx ?? defaults.hitReact?.recoilPx ?? 4,
      mode: cfg.hitReact?.mode ?? defaults.hitReact?.mode ?? 'flash',
    },
    death: {
      mode: cfg.death?.mode ?? defaults.death?.mode ?? 'sink',
      durMs: cfg.death?.durMs ?? defaults.death?.durMs ?? 600,
    },
    telegraph: {
      tellMs: cfg.telegraph?.tellMs ?? defaults.telegraph?.tellMs ?? 300,
      cue: cfg.telegraph?.cue ?? defaults.telegraph?.cue ?? 'flash',
    },
    vibe: {
      auraColor: cfg.vibe?.auraColor ?? defaults.vibe?.auraColor,
      auraAlpha: cfg.vibe?.auraAlpha ?? defaults.vibe?.auraAlpha ?? 0,
      particleKind: cfg.vibe?.particleKind ?? defaults.vibe?.particleKind,
    },
  };
}

// ── Coil Roster Registry ──────────────────────────────────────────────

/**
 * The Hollow Coil mob roster → visual archetype configs.
 *
 * Emotional gradient: cozy-bright (pig_beach) → grey-tense (brackenmoor) →
 * spooky-dread (coil_undercroft). The archetype + vibe carry the tone:
 * friendly fauna have no aura; spooky undead have a faint purple/green aura
 * that reads "wrong."
 */
const COIL_ROSTER: Record<string, MobVisualConfig> = {
  // ── pig_beach: cozy fauna (easy, L1-3) — warm, friendly vibes ──
  pig: {
    archetype: 'grounder',
    art: { svgKey: 'pig', tint: 0xffb6c1 },
    // Passive — never telegraphs in practice; gentle Tier-1 breathe.
  },
  snail: {
    archetype: 'grounder',
    art: { svgKey: 'snail', tint: 0x87ceeb },
    idle: { bobAmpPx: 1.5, bobPeriodMs: 2600, sway: 0.5 }, // slowest breather
    hitReact: { recoilPx: 5, squash: 0.12 }, // shelled — barely budges
  },
  slime: {
    archetype: 'grounder',
    art: { svgKey: 'slime', tint: 0x32cd32 },
    idle: { bobAmpPx: 3, bobPeriodMs: 1400 }, // jiggle bob
    hitReact: { squash: 0.3 }, // blobby — big squash
    death: { mode: 'dissolve', durMs: 700 }, // melts away, not sinks
  },
  // What a slime splits into (plan 08 phase 4): the slime's art, lighter and
  // twitchier.
  slimeling: {
    archetype: 'grounder',
    art: { svgKey: 'slimeling', tint: 0x7cfc00 },
    idle: { bobAmpPx: 2, bobPeriodMs: 900 },
    hitReact: { squash: 0.35 },
    death: { mode: 'dissolve', durMs: 500 },
  },
  jellyfish: {
    archetype: 'floater',
    art: { svgKey: 'jellyfish', tint: 0xffaaff },
    idle: { bobAmpPx: 5, bobPeriodMs: 2000, sway: 4 }, // slow, gentle drift
    hitReact: { recoilPx: 4, squash: 0.15 }, // soft recoil
    death: { mode: 'dissolve', durMs: 800 }, // gentle dissolve upward
    vibe: { particleKind: 'sparkle' }, // pale bioluminescent trail
  },

  // ── brackenmoor: moderate threats (L4-6) — the tension ramp ──
  wild_boar: {
    // ANIMATION_REFERENCE: grounder — bobs less but sways more (alert),
    // sturdy recoil, heavier sink, PROMINENT 1s crouch telegraph.
    archetype: 'grounder',
    art: { svgKey: 'wild_boar', tint: 0x8b6c4a, scale: 1.15 },
    idle: { bobAmpPx: 1, bobPeriodMs: 1600, sway: 2 },
    hitReact: { squash: 0.15, recoilPx: 4 }, // sturdy — half the recoil
    telegraph: { tellMs: 1000, cue: 'crouch' },
    vibe: { particleKind: 'heavy_dust' }, // grey dust — alert, aggressive
  },
  giant_bee: {
    // ANIMATION_REFERENCE: floater — buzzy fast bob, quick dissolve.
    archetype: 'floater',
    art: { svgKey: 'giant_bee', tint: 0xffd700 },
    idle: { bobAmpPx: 4, bobPeriodMs: 500, sway: 2 }, // buzzing
    hitReact: { recoilPx: 8 }, // sharp — it's fast
    death: { mode: 'dissolve', durMs: 400 }, // buzzes out quickly
    vibe: { particleKind: 'pollen' }, // faint gold pollen trail
  },
  mushroom: {
    archetype: 'grounder',
    art: { svgKey: 'mushroom', tint: 0xffa500 },
    idle: { bobAmpPx: 0.5, bobPeriodMs: 3000, sway: 0 }, // rooted — barely moves
    hitReact: { recoilPx: 2, squash: 0.1 }, // rooted — barely recoils
    death: { mode: 'shatter', durMs: 700 }, // cap shatters, not sinks
    vibe: { particleKind: 'spore' },
  },
  shadow_bat: {
    // ANIMATION_REFERENCE: floater (erratic, spectral) — phases through
    // hits instead of flashing; dissolves into green wisps (the corruption).
    archetype: 'floater',
    art: { svgKey: 'shadow_bat', tint: 0x3a2a4a },
    idle: { bobAmpPx: 6, bobPeriodMs: 900, sway: 5 }, // erratic drift
    hitReact: { mode: 'phase', recoilPx: 6 },
    death: { mode: 'dissolve', durMs: 600 },
    vibe: { auraColor: 0x39b06e, auraAlpha: 0.12, particleKind: 'wisp' }, // green corruption wisps
  },
  evil_eye: {
    archetype: 'phantom',
    art: { svgKey: 'evil_eye', tint: 0x4a2a6a },
    idle: { bobPeriodMs: 800 }, // ethereal pulse (~2s alpha cycle)
    vibe: { auraColor: 0x9400d3, auraAlpha: 0.15, particleKind: 'eldritch_glow' }, // purple, watching
  },

  // ── coil_undercroft: challenging (L6-8) — the climax ──
  dark_stump: {
    archetype: 'lumberer',
    art: { svgKey: 'dark_stump', tint: 0x2a3a2a, scale: 1.1 },
    idle: { bobPeriodMs: 4000, sway: 2 }, // sways like a heavy tree
    death: { mode: 'shatter', durMs: 1000 }, // breaks into wood chunks
    vibe: { auraColor: 0x2d5a1f, auraAlpha: 0.1, particleKind: 'root_chips' },
  },
  hollow_keeper: {
    // THE BOSS — lumberer (HOLLOW_KEEPER_BOSS_SPEC.md): armor with no one
    // inside, rigid but off-balance. Phase overlays (P2/P3) speed the sway,
    // pulse the aura, and shorten the telegraph — see HOLLOW_KEEPER_PHASES.
    archetype: 'lumberer',
    art: { svgKey: 'hollow_keeper', tint: 0x696969, scale: 1.3 },
    idle: { bobAmpPx: 0, bobPeriodMs: 4000, sway: 2 },
    hitReact: { squash: 0.1, flashColor: 0xffffff, recoilPx: 2 }, // barely moves — massive
    death: { mode: 'shatter', durMs: 5000 }, // the 5s cinematic death beat
    telegraph: { tellMs: 1500, cue: 'crouch' },
    vibe: { auraColor: 0x00ff7f, auraAlpha: 0.15, particleKind: 'corruption_flecks' }, // the sickly green
  },
  cursed_armor: {
    archetype: 'lumberer',
    art: { svgKey: 'hollow_keeper', tint: 0x555555, scale: 1.0 },
    vibe: { auraColor: 0x333355, auraAlpha: 0.08 },
  },
};

// ── Nexus Roster Registry ─────────────────────────────────────────────

/** Biome Nexus tour-world roster — appended lane-by-lane (volcanic → ruins). */
const NEXUS_ROSTER: Record<string, MobVisualConfig> = {
  magma_golem: {
    archetype: 'lumberer',
    art: { svgKey: 'magma_golem', tint: 0xffffff },
    vibe: { auraColor: 0xff5a1e, auraAlpha: 0.12, particleKind: 'ember' },
  },
  obsidian_sentinel: {
    archetype: 'lumberer',
    art: { svgKey: 'obsidian_sentinel', tint: 0xffffff },
    idle: { bobAmpPx: 0, bobPeriodMs: 5000, sway: 1 }, // stiller than stone
    vibe: { auraColor: 0x6a4a9a, auraAlpha: 0.12 },
  },
  infernal: {
    archetype: 'lumberer',
    art: { svgKey: 'infernal', tint: 0xffffff, scale: 1.15 },
    telegraph: { tellMs: 1200, cue: 'grow' },
    vibe: { auraColor: 0xff8a3c, auraAlpha: 0.18, particleKind: 'ember' },
  },
  fire_boar: {
    archetype: 'grounder',
    art: { svgKey: 'fire_boar', tint: 0xffffff, scale: 1.1 },
    idle: { bobAmpPx: 1, bobPeriodMs: 1600, sway: 2 },
    telegraph: { tellMs: 1000, cue: 'crouch' },
    vibe: { particleKind: 'ember' },
  },
  lava_slug: {
    archetype: 'grounder',
    art: { svgKey: 'lava_slug', tint: 0xffffff },
    idle: { bobAmpPx: 1.5, bobPeriodMs: 2800, sway: 0.5 },
    death: { mode: 'dissolve', durMs: 800 }, // melts
    vibe: { auraColor: 0xff6600, auraAlpha: 0.1 },
  },
  flame_imp: {
    archetype: 'skitterer', // first skitterer in the game — defaults from ARCHETYPE_DEFAULTS
    art: { svgKey: 'flame_imp', tint: 0xffffff },
    vibe: { auraColor: 0xff8a3c, auraAlpha: 0.1, particleKind: 'ember' },
  },
  ice_drake: {
    archetype: 'floater',
    art: { svgKey: 'ice_drake', tint: 0xffffff },
    idle: { bobAmpPx: 3, bobPeriodMs: 1800, sway: 2 },
    vibe: { particleKind: 'frost' },
  },
  snow_wolf: {
    archetype: 'grounder',
    art: { svgKey: 'snow_wolf', tint: 0xffffff },
    idle: { bobAmpPx: 1, bobPeriodMs: 1500, sway: 2 }, // alert pacer
    telegraph: { tellMs: 600, cue: 'crouch' },
  },
  frost_elemental: {
    archetype: 'floater',
    art: { svgKey: 'frost_elemental', tint: 0xffffff, anchorY: 0.6 }, // floats — anchor above feetline
    idle: { bobAmpPx: 5, bobPeriodMs: 2400, sway: 3 },
    death: { mode: 'shatter', durMs: 700 }, // ice shatters
    vibe: { auraColor: 0x70d4ff, auraAlpha: 0.14, particleKind: 'frost' },
  },
  crystal_spider: {
    archetype: 'skitterer', // brittle scuttle — poof death, fast flash telegraph
    art: { svgKey: 'crystal_spider', tint: 0xffffff },
  },
  cave_bat: {
    archetype: 'floater',
    art: { svgKey: 'cave_bat', tint: 0xffffff },
    idle: { bobAmpPx: 5, bobPeriodMs: 700, sway: 4 }, // erratic flapper
  },
  stone_golem: {
    archetype: 'lumberer', // dormant stone — no vibe layer: its ONLY accent is the moss
    art: { svgKey: 'stone_golem', tint: 0xffffff },
  },
  moonfall_stone_golem: {
    // Moonfall Hollow's mini-boss (plan 07): dedicated StoneGolemRenderer art
    // (the stone_golem elsewhere keeps the generic golem frame), a slow heavy
    // idle that sheds dust, a rise-and-slam wind-up, and a long crumbling
    // death. Phase 2 overlay: STONE_GOLEM_PHASES.
    archetype: 'lumberer',
    art: { svgKey: 'moonfall_stone_golem', tint: 0xffffff },
    idle: { bobAmpPx: 0, bobPeriodMs: 4500, sway: 1.5 },
    hitReact: { squash: 0.12, flashColor: 0xffffff, recoilPx: 1 }, // barely rocks
    death: { mode: 'shatter', durMs: 1800 },
    telegraph: { tellMs: 900, cue: 'slam' },
    vibe: { auraColor: 0x3ee8c0, auraAlpha: 0.06, particleKind: 'rubble_dust' },
  },
  gem_golem: {
    archetype: 'lumberer',
    art: { svgKey: 'gem_golem', tint: 0xffffff },
    vibe: { auraColor: 0xe6b800, auraAlpha: 0.1, particleKind: 'sparkle' }, // treasure-hoard glint
  },
  crystal_elemental: {
    archetype: 'floater',
    art: { svgKey: 'crystal_elemental', tint: 0xffffff, anchorY: 0.6 }, // floats — anchor above feetline
    death: { mode: 'shatter', durMs: 700 }, // crystal shatters
    vibe: { auraColor: 0x7fffd4, auraAlpha: 0.14 },
  },
  void_wraith: {
    archetype: 'phantom', // defaults: phase hit-react, dissolve death, flicker telegraph
    art: { svgKey: 'void_wraith', tint: 0xffffff, anchorY: 0.6 }, // floats — anchor above feetline
    vibe: { auraColor: 0xb34ab3, auraAlpha: 0.16, particleKind: 'wisp' },
  },
  shadow_stalker: {
    archetype: 'phantom',
    art: { svgKey: 'shadow_stalker', tint: 0xffffff },
    idle: { bobAmpPx: 1, bobPeriodMs: 1000, sway: 1 }, // coiled breath — the barely-moving pounce hold
    vibe: { auraColor: 0x9a4ad1, auraAlpha: 0.12 },
  },
  phoenix: {
    archetype: 'floater',
    art: { svgKey: 'phoenix', tint: 0xffffff },
    idle: { bobAmpPx: 4, bobPeriodMs: 1600, sway: 3 },
    death: { mode: 'dissolve', durMs: 900 }, // burns out
    vibe: { auraColor: 0xff8c00, auraAlpha: 0.16, particleKind: 'ember' },
  },
  ancient_treant: {
    archetype: 'lumberer', // defaults carry the shatter death + crouch telegraph
    art: { svgKey: 'ancient_treant', tint: 0xffffff },
    idle: { bobPeriodMs: 4200, sway: 2 }, // the slowest sway in the roster
    vibe: { auraColor: 0x2e8b57, auraAlpha: 0.08 },
  },
  war_golem: {
    archetype: 'lumberer',
    art: { svgKey: 'war_golem', tint: 0xffffff },
    vibe: { auraColor: 0xff3300, auraAlpha: 0.08 },
  },

  // ── gap fill (docs/design/biomes/sheets/gapfill.md) — the last 3 pig fallbacks ──
  stump: {
    // Deliberately the plainest lumberer in the roster — zero overrides. The
    // contrast with dark_stump is config-level too: the elite earns bespoke
    // tint/scale/aura; the normal takes the archetype defaults unmodified.
    archetype: 'lumberer',
    art: { svgKey: 'stump', tint: 0xffffff },
  },
  forest_troll: {
    archetype: 'grounder',
    art: { svgKey: 'forest_troll', tint: 0xffffff },
    idle: { bobAmpPx: 1.5, bobPeriodMs: 1800, sway: 2 }, // low bob, lurking sway — watched-before-charged
  },
  void_walker: {
    archetype: 'phantom', // keeps the corruption family's phase/dissolve/flicker feel
    art: { svgKey: 'void_walker', tint: 0xffffff }, // grounded — NO anchorY float (the wraith contrast)
    idle: { bobAmpPx: 1, bobPeriodMs: 1100, sway: 1 }, // stride-bob vs the wraith's dead stillness
    vibe: { auraColor: 0xb34ab3, auraAlpha: 0.14, particleKind: 'wisp' }, // wraith's rim hex, less ghostly
  },
};

/** Get the resolved visual config for a mob type, or null if not in the roster. */
export function getMobVisualConfig(mobType: string): ResolvedMobVisualConfig | null {
  const cfg = COIL_ROSTER[mobType] ?? NEXUS_ROSTER[mobType] ?? BRANCH_MOB_VISUALS[mobType];
  if (!cfg) return null;
  return resolveMobVisualConfig(cfg);
}

/** All mob types in the Coil roster (for audits / mob_shot iteration). */
export const COIL_MOB_TYPES = Object.keys(COIL_ROSTER);

/** All mob types in the Nexus roster (for audits / biome_shot iteration). */
export const NEXUS_MOB_TYPES = Object.keys(NEXUS_ROSTER);

// ── Hollow Keeper boss phases ─────────────────────────────────────────

/**
 * Per-phase behavior overlay for the Hollow Keeper (the boss gets the most
 * love). The base config above is Phase 1; on each server `bossPhaseChange`
 * broadcast the renderer re-textures via the phase-exact SVG palette
 * (HollowKeeperRenderer P1/P2/P3) AND applies this overlay:
 * agitated sway → spasmodic sway, pulsing aura, shortening telegraph.
 *
 * Source: ANIMATION_REFERENCE.md "The boss — Hollow Keeper animation
 * overlay" + HOLLOW_KEEPER_BOSS_SPEC.md phases.
 */
export interface BossPhaseOverlay {
  /** Idle sway period — 4s tired → 2.5s agitated → 1.5s spasmodic. */
  idlePeriodMs: number;
  /** Base aura opacity for the phase. */
  auraAlpha: number;
  /** Aura pulse frequency in Hz (0 = steady). */
  auraPulseHz: number;
  /** Telegraph crouch duration — 1.5s → 1s → 0.7s. */
  tellMs: number;
}

/** @deprecated name — the overlay is shared by every phased boss renderer. */
export type KeeperPhaseOverlay = BossPhaseOverlay;

export const HOLLOW_KEEPER_PHASES: Record<1 | 2 | 3, BossPhaseOverlay> = {
  1: { idlePeriodMs: 4000, auraAlpha: 0.15, auraPulseHz: 0, tellMs: 1500 },
  2: { idlePeriodMs: 2500, auraAlpha: 0.15, auraPulseHz: 0.8, tellMs: 1000 },
  3: { idlePeriodMs: 1500, auraAlpha: 0.25, auraPulseHz: 2, tellMs: 700 },
};

/**
 * Map a server phase name (content/mobs/starter_zone.toml
 * `[[hollow_keeper.phases]]`) to the visual phase index.
 * Unknown names fall back to phase 1 (base presentation).
 */
export function keeperPhaseFromName(phaseName: string | undefined): 1 | 2 | 3 {
  switch (phaseName) {
  case 'Fogbound': return 2;
  case 'Hollow Wrath': return 3;
  default: return 1;
  }
}

// ── Stone Golem phases (plan 07) ──────────────────────────────────────

/** Phase 1 "Dormant Guardian" → phase 2 "Awakened Core" (below 50% HP):
 *  a faster, restless sway, a brighter pulsing aura, a quicker slam tell. */
export const STONE_GOLEM_PHASES: Record<1 | 2, BossPhaseOverlay> = {
  1: { idlePeriodMs: 4500, auraAlpha: 0.06, auraPulseHz: 0, tellMs: 900 },
  2: { idlePeriodMs: 2800, auraAlpha: 0.2, auraPulseHz: 1, tellMs: 650 },
};

